/**
 * Local persistence for the terminal chat transcript, keyed per paradigm
 * user. Mirrors the localStorage conventions in `agent-url.ts`.
 *
 * Goal: Telegram-style continuity. A reload paints the most recent messages
 * immediately so the user has something to scroll while the gateway
 * reconnects.
 *
 * OpenClaw's `chat.history` RPC is a tail-reader with no cursor pagination
 * (hard max 1000 messages), so the local cache is the primary source of
 * deeper history — caps are intentionally generous to retain weeks of
 * conversation across reloads.
 */

import type { ChatMessage } from './types';

const HISTORY_KEY_PREFIX = 'paradigm_terminal_chat_history';

/**
 * How long persisted messages stay in localStorage. 30 days keeps roughly a
 * month of conversation visible across reloads — important because OpenClaw's
 * `chat.history` RPC has no cursor pagination, so anything dropped from this
 * cache and rolled out of the gateway's 1000-message tail is unrecoverable.
 */
export const PERSISTED_HISTORY_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Lower bound on persisted messages so a long-idle user (vacation, long
 * weekend) still sees their last conversation on reload even if it's older
 * than the retention window.
 */
export const PERSISTED_HISTORY_MIN_MESSAGES = 50;

/**
 * Absolute safety cap. Per-origin localStorage caps at ~5MB and a single
 * chat message can easily exceed 1KB (assistant replies with full
 * portfolio analyses run several KB each). Keep this bounded well below
 * the 5MB origin budget so other features sharing localStorage have room.
 */
export const PERSISTED_HISTORY_HARD_CAP = 1500;

const historyKey = (userId: string): string =>
  `${HISTORY_KEY_PREFIX}:${userId.toLowerCase()}`;

export function loadChatHistory(userId: string): readonly ChatMessage[] {
  try {
    const raw = localStorage.getItem(historyKey(userId));
    if (raw == null || raw.length === 0) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isPersistedChatMessage);
  } catch {
    // Corrupt blob or localStorage unavailable — treat as no history.
    return [];
  }
}

export function saveChatHistory(
  userId: string,
  messages: readonly ChatMessage[],
): void {
  try {
    const trimmed = trimForPersistence(messages);
    localStorage.setItem(historyKey(userId), JSON.stringify(trimmed));
  } catch {
    // localStorage full / unavailable — best-effort persistence.
  }
}

/**
 * Pick the slice of `messages` we want to persist:
 *
 *   1. Drop any message older than `PERSISTED_HISTORY_MAX_AGE_MS` so the
 *      blob doesn't accumulate forever.
 *   2. Always keep at least `PERSISTED_HISTORY_MIN_MESSAGES` of the most
 *      recent messages — so a long-idle user still sees their last
 *      conversation on reload.
 *   3. Hard-cap the total at `PERSISTED_HISTORY_HARD_CAP`.
 *   4. Strip the streaming flag — a partially-streamed bubble persisted
 *      with `isStreaming: true` would reload as stuck dots.
 */
function trimForPersistence(
  messages: readonly ChatMessage[],
): readonly ChatMessage[] {
  if (messages.length === 0) return messages;

  const cutoff = Date.now() - PERSISTED_HISTORY_MAX_AGE_MS;
  const withinWindow = messages.filter((m) => m.timestamp > cutoff);

  const kept =
    withinWindow.length >= PERSISTED_HISTORY_MIN_MESSAGES
      ? withinWindow
      : messages.slice(-PERSISTED_HISTORY_MIN_MESSAGES);

  return kept
    .slice(-PERSISTED_HISTORY_HARD_CAP)
    .map((m) => (m.isStreaming === true ? { ...m, isStreaming: false } : m));
}

export function clearChatHistory(userId: string): void {
  try {
    localStorage.removeItem(historyKey(userId));
  } catch {
    // ignore
  }
}

function isPersistedChatMessage(value: unknown): value is ChatMessage {
  if (value == null || typeof value !== 'object') return false;
  const m = value as Record<string, unknown>;
  return (
    typeof m.id === 'string' &&
    (m.role === 'user' || m.role === 'assistant') &&
    typeof m.content === 'string' &&
    typeof m.timestamp === 'number'
  );
}
